import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule, DxDataGridComponent, DxDataGridModule, DxDropDownBoxModule, DxTagBoxModule, DxToastModule, DxTreeViewModule } from 'devextreme-angular';
import { ConfigService } from 'src/app/services/config.service';
import { DeletePopupComponent } from 'src/app/components/delete-popup/delete-popup.component';
import { DxTreeViewComponent, DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { AppGlobals } from 'src/app/app.globals';
import { environment } from 'src/environments/environment';

import { ActivityLogService } from 'src/app/services/activity-log.service';
import { ActivitySection } from 'src/app/constants/activity-log';
import { SearchableDropdownDirective } from '../../../directives/searchable-dropdown.directive';
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
@Component({
    selector: 'app-export-config',
    imports: [
    GpdButtonComponent,
    SvgIconComponent,CommonModule, DxButtonModule, DxDataGridModule, DxDropDownBoxModule, DxTagBoxModule, DeletePopupComponent, DxTreeViewModule, DxToastModule, SearchableDropdownDirective],
    templateUrl: './export-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./export-config.component.css']
})
export class ExportConfigComponent implements OnInit {

  @ViewChild('exportConfigGrid', { static: false }) exportConfigGrid: DxDataGridComponent
  @ViewChild('treeView', { static: false }) treeView: DxTreeViewComponent;

  confirmText: any = $localize`:@@confirm:Confirm`;
  cancelText: any = $localize`:@@cancel:Cancel`;


  exportConfig: any;
  exportConfigTasks: any;
  exportConfigSectionItems: any[] = [];
  private sectionSummaries = new WeakMap<string[], string>();

  editMode = false;

  itemToDelete: any;
  deletePopupVisible = false;
  deleteMessage = 'Are you sure you want to delete this Export-Group?';

  newItem: any;

  toastVisible = false;

  constructor(private configService: ConfigService, private appGlobals: AppGlobals, private activityLog: ActivityLogService) { }

  ngOnInit(): void {
    this.activityLog.logView({ Section: ActivitySection.CONFIG_TASK_EXPORT });

/*     if (environment.isTestEnvironment === true) { */
      this.appGlobals.SetGlobalMenuIndex(8);
/*     } else {
      this.appGlobals.SetGlobalMenuIndex(7);
    } */


    this.getPageData();
  }

  getPageData() {
    this.configService.getExportConfig({}).then((result) => {
      this.exportConfig = result.exportConfigItems;
      this.exportConfigTasks = result.exportConfigTaskItems;
      this.exportConfigSectionItems = result.exportConfigSectionItems ?? [];
      this.exportConfig?.forEach(i => i.sectionKeys ??= []);
    });
  }

  addNewGroup(e) {
    this.exportConfigGrid.instance.addRow();
  }

  initDelete(item) {
    this.itemToDelete = item;
    this.deletePopupVisible = true;
  }

  editRow(taskId: any) {
    let visibleRows = this.exportConfigGrid.instance.getVisibleRows();
    let rowIndex = visibleRows.find((row: any) => row.data.id == taskId).rowIndex;
    this.exportConfigGrid.instance.editRow(rowIndex);
  }

  onDropDownBoxValueChanged(values) {
    this.updateSelection(this.treeView?.instance, values);
  }

  deleteExportGroup(e) {
    this.configService.deleteExportConfig({ ExportConfigItem: this.itemToDelete }).then(() => {
      this.deletePopupVisible = false;
      this.exportConfig.splice(this.exportConfig.indexOf(this.itemToDelete), 1);
    });
  }

  onTreeViewReady(e: DxTreeViewTypes.ContentReadyEvent, values) {
    this.updateSelection(e.component, values);
  }

  updateSelection(treeView: DxTreeViewComponent['instance'], values) {
    if (!treeView) return;

    if (!values) {
      treeView.unselectAll();
    }

    values?.forEach(((value) => {
      treeView.selectItem(value);
    }));
  }

  // Staged through the grid edit cell like the sections: CANCEL discards the ticks, CONFIRM carries them (e.changes).
  // Ticks are tracked from the event deltas, not getSelectedNodeKeys(), which drops ticks made under another search term.
  // A node carries its descendants; ancestors follow their children (all children ticked = ticked).
  onTreeViewSelectionChanged(e: DxTreeViewTypes.ItemSelectionChangedEvent, item: any) {
    const keys = new Set<any>(item.value ?? []);
    const mark = (n: any) => {
      n.selected ? keys.add(n.key) : keys.delete(n.key);
      n.children?.forEach(mark);
    };
    mark(e.node);
    for (let p: any = e.node.parent; p; p = p.parent) {
      p.children.every((c: any) => keys.has(c.key)) ? keys.add(p.key) : keys.delete(p.key);
    }
    item.value = [...keys];
    item.setValue(item.value);
  }

  // A CONFIRM without changes also fires onSaved; nothing to post then.
  onSaved(e: any) {
    if (e?.changes?.length) this.saveExportGroup();
  }

  private isGroup(key: string) {
    return this.exportConfigSectionItems.some(i => i.parentKey === key);
  }

  // Ticks are tracked from the event deltas, not getSelectedNodeKeys(), which drops ticks made under another search term.
  // item is the grid's edit-cell info: setValue stages the change so CANCEL discards it and CONFIRM carries it.
  onSectionSelectionChanged(e: any, item: any) {
    const keys = new Set<string>(item.value ?? []);
    const apply = (n: any) => {
      if (n.children?.length) n.children.forEach(apply);
      else if (!this.isGroup(n.key)) n.selected ? keys.add(n.key) : keys.delete(n.key);
    };
    apply(e.node);
    item.value = [...keys]; // setValue does not refresh the template's snapshot; later ticks in this edit build on it
    item.setValue(item.value);
  }

  // The tree is built when the popup first opens; tick the stored keys then (re-applying them via onSectionSelectionChanged is a no-op).
  onSectionTreeReady(e: any, keys: string[]) {
    keys?.forEach(k => e.component.selectItem(k));
  }

  getSectionSummary(row: any): string {
    const keys: string[] = row.sectionKeys ?? [];
    let text = this.sectionSummaries.get(keys);
    if (text === undefined) {
      const byKey = new Map(this.exportConfigSectionItems.map(i => [i.key, i]));
      const counts = new Map<string, number>();
      keys.forEach(k => {
        let item = byKey.get(k);
        while (item?.parentKey) item = byKey.get(item.parentKey);
        if (item) counts.set(item.text, (counts.get(item.text) ?? 0) + 1);
      });
      text = [...counts].map(([t, n]) => `${t} (${n})`).join(' · ');
      this.sectionSummaries.set(keys, text);
    }
    return text;
  }

  getStaleText(row: any): string {
    const labels = row.staleItemLabels?.join(', ');
    return labels ? $localize`:@@exportStaleItems:No longer available and removed on save: ${labels}:labels:` : '';
  }

  saveExportGroup() {
    const items = this.exportConfig.map(i => ({
      Id: i.id,
      ExportPackageTitle: i.exportPackageTitle,
      TaskIds: i.taskIds ?? [],
      SectionKeys: (i.sectionKeys ?? []).filter(k => !this.isGroup(k)),
    }));
    return this.configService.saveExportConfig({ ExportConfigItems: items }).then((result) => {
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
      else {
        this.toastVisible = true;
        this.editMode = false;
        this.getPageData();
      }
    });
  }
}
